import { defineComponent as _defineComponent } from 'vue'
import WebViewer from '@pdftron/pdfjs-express-viewer';
import { onMounted, ref, watch, useListeners, computed } from 'vue';
import { usePermissionComposable } from '@/composables/usePermission';
import store from '@/store';
import { readUserLocale } from '@/store/main/getters';

interface ViewerHeader {
  getHeader(headerName: string): {
    push(item: {
      img: string;
      index: number;
      type: string;
      element?: string;
      onClick: () => void;
    }): void;
  };
}

interface MenuOverlayItem {
  dataElement: string;
  label?: string;
  onClick?: () => void;
  [key: string]: unknown;
}

// https://pdfjs.express/

export default /*#__PURE__*/_defineComponent({
  __name: 'PdfViewer',
  props: {
  pdf: {
    type: String,
    default: '',
  },
  fileName: {
    type: String,
    default: 'document.pdf',
  },
},
  emits: ["delete"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'delete'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;





const listeners = useListeners();
const isDeleteEvent = computed(() => Boolean(listeners.delete));

const pdfview = ref('');
let instance: WebViewer | null = null;
const loading = ref(true);

const { hasPermission, PERMISSIONS: P } = usePermissionComposable();

const mapLocaleToViewerLanguage = (locale: string): string => {
  // Map application locales to PDFjs Express supported language codes
  switch (locale) {
    case 'ja-JP':
      return 'ja';
    case 'en-US':
    default:
      return 'en';
  }
};

const updateViewerLanguage = () => {
  if (!instance || !instance.UI || !instance.UI.setLanguage) return;

  const locale = readUserLocale(store);
  const language = mapLocaleToViewerLanguage(locale);

  // Set language in PDFjs Express Viewer
  instance.UI.setLanguage(language);
};

const base64ToBlob = (base64: string): Blob | null => {
  const bin = atob(base64.replace(/^.*,/, ''));
  const buffer = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    buffer[i] = bin.charCodeAt(i);
  }
  try {
    const blob = new Blob([buffer.buffer], {
      type: 'application/pdf',
    });
    return blob;
  } catch (e) {
    return null;
  }
};

const pdfjsExpressViewerApiKey =
  process.env.VUE_APP_PDFJS_EXPRESS_VIEWER_API_KEY;

const initPdfjsExpressInstance = async () => {
  const locale = readUserLocale(store);
  const language = mapLocaleToViewerLanguage(locale);

  instance = await WebViewer(
    {
      path: './pdfjsexpress',
      licenseKey: pdfjsExpressViewerApiKey,
    },
    pdfview.value
  );

  if (isDeleteEvent.value) {
    instance.UI.setHeaderItems((header: ViewerHeader) => {
      header.getHeader('default').push({
        img: 'data:image/png;base64,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',
        index: -1,
        type: 'actionButton',
        element: 'closeButton',
        onClick: () => emits('delete'),
      });
    });
  }

  // NOTES: Hide header items
  // @see https://pdfjs.express/documentation/ui-customization/customizing-header

  // NOTES: Hide settings menu items
  // @see https://pdfjs.express/api/7.0/WebViewerInstance.MenuOverlay.html
  const menuItems = instance.settingsMenuOverlay.getItems();

  // Always remove printButton (印刷 option)
  let filteredItems = menuItems.filter((item: MenuOverlayItem) => {
    return item.dataElement !== 'printButton';
  });

  // Additionally remove downloadButton if user doesn't have permission
  if (!hasPermission(P.SDS_DOWNLOAD)) {
    filteredItems = filteredItems.filter((item: MenuOverlayItem) => {
      return item.dataElement !== 'downloadButton';
    });
  }

  instance.settingsMenuOverlay.update(filteredItems);
  instance.UI.setLanguage(language);
};

const renderPdf = async () => {
  if (!instance) {
    throw new Error('instance is not initialized');
  }
  loading.value = true;
  const doc = base64ToBlob(props.pdf);
  if (!doc) {
    throw new Error('Failed to convert base64 to blob');
  }
  const url = URL.createObjectURL(doc);
  await instance.UI.loadDocument(url, {
    filename: props.fileName,
  });
  instance.setFitMode(instance.FitMode.FitWidth);
  loading.value = false;
};

onMounted(async () => {
  pdfview.value;
  await initPdfjsExpressInstance();
  await renderPdf();

  // Watch for language changes
  store.watch(
    () => readUserLocale(store),
    () => {
      updateViewerLanguage();
    }
  );
});

watch(
  () => [props.pdf, props.fileName],
  async () => {
    if (!instance) {
      // Instance not initialized yet, wait for it
      return;
    }
    await renderPdf();
  }
);

return { __sfc: true,props, emits, listeners, isDeleteEvent, pdfview, instance, loading, hasPermission, P, mapLocaleToViewerLanguage, updateViewerLanguage, base64ToBlob, pdfjsExpressViewerApiKey, initPdfjsExpressInstance, renderPdf }
}

})